import React, { useState, useEffect } from 'react';

import { isArray, isBoolean, get } from 'lodash';
import { useTranslation } from 'react-i18next';

import MyButton from '@common/components/Button/MyButton';
import MyCheckbox from '@common/components/MyCheckbox/MyCheckbox';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { INIT_GRID_CELL_MODAL_STATE } from '@common/constants';
import {
  createPathNameForCheckbox,
  getParentPathForNodeObj,
  getShowStateForArrayWithSource,
  getShowStateForList,
  getValidPropKeyForNodeObj,
  isNodeList,
  isNodeObj,
} from '@common/helpers/placeholderModalHelpers';

const ConditionalModal = ({
  documentJson,
  showModal,
  setShowModal,
  changeTextFunc,
  currentValue = '',
  setProp,
  ifConditionalTrueHideElement,
}) => {
  const [checkedField, setCheckedField] = useState();
  const [checkBehavior, setCheckBehavior] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    setCheckedField(currentValue);
    setCheckBehavior(ifConditionalTrueHideElement);
  }, [showModal, currentValue]);

  const changeSelected = (_, data) => {
    let newValue = data.name === checkedField ? '' : data.name;
    setCheckedField(newValue);
  };

  const checkDocType = (providedDocument, propKey, modalShowState) => {
    let currentField = get(providedDocument, propKey);

    if (isNodeList(currentField, modalShowState, propKey)) {
      return getDocumentFields(
        currentField[0],
        getShowStateForList(modalShowState)
      );
    }

    if (isNodeObj(currentField, modalShowState)) {
      let validPropKey = getValidPropKeyForNodeObj(propKey);
      let parentPath = getParentPathForNodeObj({
        propKey,
        modalShowState,
      });

      return getDocumentFields(currentField, {
        ...modalShowState,
        parent: `${parentPath}${validPropKey}`,
        showRootFields: true,
      });
    }

    if (
      isArray(currentField) &&
      modalShowState.parent !== '' &&
      modalShowState.showRootFields
    ) {
      return getDocumentFields(
        currentField,
        getShowStateForArrayWithSource({
          modalShowState,
          propKey,
        })
      );
    }

    if (isBoolean(currentField)) {
      let checkboxFieldName = createPathNameForCheckbox(
        modalShowState,
        propKey
      );

      return (
        <MyCheckbox
          label={propKey}
          name={checkboxFieldName}
          parent={modalShowState.parent}
          checked={checkedField === checkboxFieldName}
          onChange={changeSelected}
        />
      );
    }

    return <div />;
  };

  const onOkClicked = () => {
    changeTextFunc(checkedField);
    setShowModal(INIT_GRID_CELL_MODAL_STATE);
  };

  const onCancel = () => {
    setCheckedField(currentValue);
    setShowModal(INIT_GRID_CELL_MODAL_STATE);
  };

  const getDocumentFields = (providedDocument = {}, modalShowState) => {
    let objKeys = Object.keys(providedDocument);
    if (showModal.show) {
      if (
        !objKeys.includes(modalShowState.listSource) &&
        !modalShowState.showRootFields
      ) {
        return <div>{t('list_is_not_found')}</div>;
      }
      return objKeys.map((propKey, index) => {
        return (
          <div key={index}>
            {checkDocType(providedDocument, propKey, modalShowState)}
          </div>
        );
      });
    }
  };

  const handleConditional = () => {
    setCheckBehavior(!checkBehavior);
    setProp((props) => {
      props.ifConditionalTrueHideElement = !checkBehavior;
    });
  };

  return (
    <Dialog open={showModal.show}>
      <DialogTitle>{t('select_condition')}</DialogTitle>
      <DialogContent>
        <Typography as='h4'>{t('condition_fields')}</Typography>
        {getDocumentFields(documentJson, showModal)}

        <Typography as='h4'>{t('condition_behavior')}</Typography>
        <MyCheckbox
          label={t('ifConditionalTrueHideElement')}
          name={'ifConditionalTrueHideElement'}
          checked={checkBehavior}
          onChange={handleConditional}
        />
      </DialogContent>
      <DialogActions>
        <MyButton onClick={onCancel}>{t('cancel')}</MyButton>
        <MyButton onClick={onOkClicked}>{t('ok')}</MyButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConditionalModal;
